import type {BiqliErrorBody} from './types';

export class BiqliError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string | null;
  readonly details: unknown;
  readonly retryable: boolean;

  constructor(options: {
    message: string;
    code?: string;
    status?: number;
    requestId?: string | null;
    details?: unknown;
    retryable?: boolean;
    cause?: unknown;
  }) {
    super(options.message);
    this.name = 'BiqliError';
    this.code = options.code ?? 'unknown_error';
    this.status = options.status ?? 0;
    this.requestId = options.requestId ?? null;
    this.details = options.details;
    this.retryable = options.retryable ?? false;
    if (options.cause !== undefined) {
      Object.defineProperty(this, 'cause', {
        value: options.cause,
        enumerable: false,
        configurable: true,
      });
    }
  }

  static fromResponse(status: number, body: BiqliErrorBody, requestId?: string | null): BiqliError {
    return new BiqliError({
      message: body.error?.message ?? body.message ?? `Biqli request failed with status ${status}.`,
      code: body.error?.code ?? 'request_failed',
      status,
      requestId: body.request_id ?? requestId ?? null,
      details: body.error?.details,
      retryable: isRetryableStatus(status),
    });
  }
}

export function isRetryableStatus(status: number): boolean {
  return status === 0 || status === 408 || status === 425 || status === 429 || status >= 500;
}

export function asBiqliError(error: unknown): BiqliError {
  if (error instanceof BiqliError) return error;
  return new BiqliError({
    message: error instanceof Error ? error.message : 'The tracking request failed.',
    code: 'network_error',
    retryable: true,
    cause: error,
  });
}
